import { type Ref, type ComponentPublicInstance, nextTick } from 'vue';

export function useScroll2bottom(_scrollContentRef: Ref<HTMLElement | ComponentPublicInstance | null>) {
    const scrollContentRef = () => {
        if (_scrollContentRef.value instanceof HTMLElement) {
            return _scrollContentRef?.value;
        }
        return _scrollContentRef?.value?.$el;
    };

    function scrollContainerScroll2bottom() {
        scrollContentRef()?.scrollTo({
            top: scrollContentRef()?.scrollHeight,
        });
    }

    async function scroll2bottom(config?: { limitBottomOffset?: number }) {
        await nextTick();
        if (config?.limitBottomOffset) {
            // 只有在 limitBottomOffset 范围内(包括等于)才会滚动到底部，否则不需要，方便在自动滚动到底部的过程中，用户可以手动滚动打断自动滚动
            // 如果用户滚动了 滚动条，则实际的偏移量会大于 limitBottomOffset，此时不会滚动到底部
            const $scrollContainer = scrollContentRef();
            if ($scrollContainer) {
                const scrollHeight = $scrollContainer.scrollHeight;
                const clientHeight = $scrollContainer.clientHeight;
                const scrollTop = $scrollContainer.scrollTop;
                const limitBottomOffset = config.limitBottomOffset;
                if (scrollHeight - clientHeight - scrollTop <= limitBottomOffset) {
                    scrollContainerScroll2bottom();
                }
            }
        } else {
            scrollContainerScroll2bottom();
        }
    }

    let oldElHeight = scrollContentRef()?.clientHeight || 0;

    async function scroll2bottomWithLimit() {
        await nextTick();
        // 获取容器高度
        const currentElHeight = scrollContentRef()?.scrollHeight || 0;
        // 如果高度发生变化，说明有新的内容追加进来了，需要滚动到底部。
        let changeHeight = currentElHeight - oldElHeight;

        if (changeHeight) {
            // 修复缩放不是 1 的情况下，高度计算不准确导致没有滚动的问题, 无法修复缩放比例过大时的问题（缩放过大整体页面也不可用了，就不考虑这种极端场景了）
            changeHeight += 1;
        }
        if (changeHeight !== 0) {
            oldElHeight = currentElHeight;
            // 滚动到底部, 但是需要限制可以滚动的最大偏移量
            // 如果用户滚动了 滚动条，则实际的偏移量会大于 limitBottomOffset，此时不会滚动到底部
            scroll2bottom({ limitBottomOffset: changeHeight });
        }
    }

    return {
        /**
         * 滚动到底部
         * @param config.limitBottomOffset: 只有在只有距离底部的距离在 limitBottomOffset 范围内(包括等于)才会滚动到底部，否则不滚动
         */
        scroll2bottom,
        /**
         * 滚动到底部，但是需要限制可以滚动的最大偏移量
         *
         * 针对有自动滚动到底部场景进行优化，在自动滚动到底部过程中，用户可以手动滚动打断自动滚动
         */
        scroll2bottomWithLimit,
    };
}
